import {memberRoleActsForTrustee} from '@sollapay/domain/rules';
import {useMutation, useQueryClient} from '@tanstack/react-query';

import {invalidateTrustAccountTeam} from '@/features/trust-accounts/utils/trust-account-invalidations.utils';
import {useApiClient} from '@/hooks';

import {trustAccountMembersApi} from '../api';

import type {TrustAccountMemberRole} from '@sollapay/enums';
import type {
  AddTrustAccountMemberPayload,
  ChangeTrustAccountMemberRolePayload
} from '@sollapay/types';

interface AccountScoped {
  trustAccountId: string;
}

/**
 * The account travels in each call's variables, so an undo kept from one account never hits another.
 * A role acting in the trustee's name always carries their power of attorney: the share panel confirms it
 * before an add or a move, and an undo or a copied team restores consent the trustee already gave.
 */
export function useTrustAccountMemberMutations() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  const onSuccess = (_data: unknown, {trustAccountId}: AccountScoped) =>
    invalidateTrustAccountTeam(queryClient, trustAccountId);

  const add = useMutation({
    mutationFn: ({
      trustAccountId,
      deferRefresh: _deferRefresh,
      ...payload
    }: AccountScoped & AddTrustAccountMemberPayload & {deferRefresh?: boolean}) =>
      trustAccountMembersApi.addMember(client, trustAccountId, withPowerOfAttorney(payload)),
    onSuccess: (data, variables) =>
      variables.deferRefresh ? undefined : onSuccess(data, variables)
  });

  const changeRole = useMutation({
    mutationFn: ({
      trustAccountId,
      userId,
      ...payload
    }: AccountScoped & ChangeTrustAccountMemberRolePayload & {userId: string}) =>
      trustAccountMembersApi.changeMemberRole(
        client,
        trustAccountId,
        userId,
        withPowerOfAttorney(payload)
      ),
    onSuccess
  });

  const remove = useMutation({
    mutationFn: ({
      trustAccountId,
      userId,
      expectedVersion
    }: AccountScoped & {userId: string; expectedVersion?: string}) =>
      trustAccountMembersApi.removeMember(client, trustAccountId, userId, expectedVersion),
    onSuccess
  });

  return {add, changeRole, remove};
}

function withPowerOfAttorney<T extends {role: TrustAccountMemberRole}>(payload: T) {
  return {...payload, powerOfAttorney: memberRoleActsForTrustee(payload.role)};
}
